import { useState, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cookie, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { apiPost } from '../lib/api';
import type { AdminUser } from '../lib/types';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');

      if (!email.trim() || !password) {
        setError('請輸入電子郵件和密碼');
        return;
      }

      setLoading(true);

      try {
        await apiPost<AdminUser>('/auth/login', { email, password });
        navigate('/admin', { replace: true });
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '登入失敗，請稍後再試';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [email, password, navigate],
  );

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* 品牌區 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-brand-gold/20 mb-4">
            <Cookie className="h-8 w-8 text-brand-gold" />
          </div>
          <h1 className="text-2xl font-bold text-brand-cocoa font-heading">
            怡味屋
          </h1>
          <p className="mt-1 text-sm text-gray-500">管理後台</p>
        </div>

        {/* 登入表單 */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl border border-brand-bake p-6 shadow-sm space-y-5"
        >
          <h2 className="text-lg font-semibold text-brand-cocoa text-center">
            管理員登入
          </h2>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* 電子郵件 */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              電子郵件
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@yiweiwu.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-brand-bake bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              />
            </div>
          </div>

          {/* 密碼 */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              密碼
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="請輸入密碼"
                className="w-full pl-10 pr-12 py-2.5 rounded-lg border border-brand-bake bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* 送出 */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-brand-gold text-white font-medium text-sm hover:bg-brand-gold/90 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? '登入中...' : '登入'}
          </button>
        </form>
      </div>
    </div>
  );
}
